/**
 * The approval queue for one ad account (audits/2026-10-26/approval-queue-spec.md, §7): what
 * the advisor proposes, Approve and Reject on what is waiting, and what was decided.
 *
 * 🔴 Approve sends nothing itself from here: it asks the worker, which sends the row's request
 * through the route it names, as the person who clicked. What comes back is that route's
 * answer, shown as the route gave it; a click that answers anything is never repeated by this
 * screen.
 *
 * The one-screen layout (audits/2026-10-29/gui/plan.md, PR 6): what is waiting is a paged list
 * on the left, the row chosen is the panel on the right with Approve and Reject in its header,
 * and what was decided is a paged list under the queue. The words are approvals-copy.ts as it
 * stands; only where they sit has changed.
 *
 * 🔴 The panel keeps the row it was opened on. After a click the row is decided and leaves the
 * queue; the panel then shows it decided, with the route's answer and no buttons, rather than
 * moving to the next waiting row under a pointer that is still over Approve.
 */
import { useEffect, useState } from "react";
import { type Approval, api } from "../lib/api.ts";
import {
	APPROVALS_ABOUT,
	APPROVALS_TITLE,
	APPROVE_SENDS,
	HISTORY_EMPTY,
	HISTORY_HEADING,
	NOTHING_CHOSEN,
	NOT_YOUR_OWN,
	QUEUE_EMPTY,
	QUEUE_UNAVAILABLE,
	actionWords,
	changeLines,
	expiresWords,
	originWords,
	resultWords,
	statusWords,
} from "../lib/approvals-copy.ts";
import { NEXT_PAGE, PREVIOUS_PAGE, pageLabel } from "../lib/table-copy.ts";
import {
	type PageSlice,
	atFirstPage,
	atLastPage,
	nextPage,
	pageOf,
	previousPage,
} from "../lib/table-page.ts";
import { useLoad } from "../lib/useLoad.ts";
import { Badge, Button, InfoBanner, Page, Panel, Select } from "../ui/index.ts";

/** Rows per page in each list. How many fit at 1280×800 is not measured. */
const QUEUE_PAGE = 8;
const HISTORY_PAGE = 6;

/** Under a paged list: which rows are shown, of the rows read, and the two page buttons. */
function Pager<T>({ shown, onPage }: { shown: PageSlice<T>; onPage: (page: number) => void }) {
	if (shown.pages <= 1) return null;
	return (
		<>
			<span>{pageLabel(shown.from, shown.to, shown.read)}</span>
			<span className="ml-auto flex gap-1">
				<button
					type="button"
					disabled={atFirstPage(shown)}
					onClick={() => onPage(previousPage(shown))}
					className="rounded px-2 py-0.5 hover:bg-surface-2 hover:text-content disabled:opacity-40"
				>
					{PREVIOUS_PAGE}
				</button>
				<button
					type="button"
					disabled={atLastPage(shown)}
					onClick={() => onPage(nextPage(shown))}
					className="rounded px-2 py-0.5 hover:bg-surface-2 hover:text-content disabled:opacity-40"
				>
					{NEXT_PAGE}
				</button>
			</span>
		</>
	);
}

export function Approvals() {
	const [accounts] = useLoad(() => api.xAccounts());
	const list = accounts.state === "ready" ? accounts.data.accounts : [];
	const [accountId, setAccountId] = useState("");
	const account = list.find((a) => a.id === accountId) ?? null;
	const xAccountId = account?.xAccountId ?? "";
	useEffect(() => {
		if (!accountId && accounts.state === "ready" && accounts.data.accounts.length > 0) {
			setAccountId(accounts.data.accounts[0].id);
		}
	}, [accountId, accounts]);

	const [queue, reload] = useLoad(() =>
		xAccountId ? api.approvals(xAccountId) : Promise.resolve(null),
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: a different account is the reason to read again
	useEffect(() => {
		reload();
	}, [xAccountId, reload]);

	/** The answer of the last click, per row: the route's own status and words. */
	const [answers, setAnswers] = useState<Record<string, { status: number; text: string }>>({});
	const [busy, setBusy] = useState<string | null>(null);
	const [chosenId, setChosenId] = useState<string | null>(null);
	const [queuePage, setQueuePage] = useState(0);
	const [historyPage, setHistoryPage] = useState(0);

	async function decide(row: Approval, how: "approve" | "reject") {
		setChosenId(row.id);
		setBusy(row.id);
		try {
			const a =
				how === "approve" ? await api.approveApproval(row.id) : await api.rejectApproval(row.id);
			const body = (typeof a.body === "object" && a.body !== null ? a.body : {}) as {
				error?: string;
				approval?: Approval;
				result?: { httpStatus: number; body: unknown };
			};
			const text =
				resultWords(body.result ?? null) ??
				body.error ??
				(body.approval ? statusWords(body.approval.status) : `HTTP ${a.status}`);
			setAnswers((prev) => ({ ...prev, [row.id]: { status: a.status, text } }));
		} catch (e) {
			setAnswers((prev) => ({
				...prev,
				[row.id]: { status: 0, text: e instanceof Error ? e.message : String(e) },
			}));
		} finally {
			setBusy(null);
			reload();
		}
	}

	const now = new Date();
	const rows = queue.state === "ready" && queue.data ? queue.data.approvals : [];
	const waiting = rows.filter((r) => r.status === "pending");
	const decided = rows.filter((r) => r.status !== "pending");
	const chosen =
		rows.find((r) => r.id === chosenId) ?? (chosenId === null ? waiting[0] : undefined);
	const queueShown = pageOf(waiting, QUEUE_PAGE, queuePage);
	const historyShown = pageOf(decided, HISTORY_PAGE, historyPage);

	return (
		<Page
			fill
			title={APPROVALS_TITLE}
			description={APPROVALS_ABOUT}
			actions={
				<Select
					label="Ad account"
					value={accountId}
					onChange={(e) => {
						setAccountId(e.target.value);
						setAnswers({});
						setChosenId(null);
						setQueuePage(0);
						setHistoryPage(0);
					}}
					placeholder="Choose an ad account"
					disabled={list.length === 0}
					options={list.map((a) => ({ value: a.id, label: `${a.name} · ${a.xAccountId}` }))}
				/>
			}
		>
			{/*
			 * Two columns, each as tall as its panels (3d, 30.10: the empty panels stood the
			 * window's height): Waiting with Decided and expired under it, the picked request
			 * beside them. A pager's footer only when there is a page to turn.
			 */}
			<div className="grid h-full min-h-0 grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] grid-rows-[minmax(0,1fr)] gap-3">
				<div className="flex min-h-0 flex-col gap-3">
					<Panel
						title={statusWords("pending")}
						footer={
							queueShown.pages > 1 ? <Pager shown={queueShown} onPage={setQueuePage} /> : undefined
						}
					>
						{queue.state === "failed" && (
							<InfoBanner variant="danger" title="The queue could not be read.">
								{queue.why}
							</InfoBanner>
						)}
						{queue.state === "ready" && queue.data?.unavailable && (
							<InfoBanner variant="warning" title={QUEUE_UNAVAILABLE}>
								{queue.data.unavailable}
							</InfoBanner>
						)}
						{queue.state === "ready" &&
							queue.data &&
							!queue.data.unavailable &&
							waiting.length === 0 && <p className="text-sm text-muted">{QUEUE_EMPTY}</p>}
						<ul className="grid gap-1">
							{queueShown.rows.map((row) => (
								<li key={row.id}>
									<button
										type="button"
										onClick={() => setChosenId(row.id)}
										aria-current={chosen?.id === row.id ? "true" : undefined}
										className={`grid w-full gap-0.5 rounded-md px-2 py-1.5 text-left hover:bg-surface-2 ${
											chosen?.id === row.id ? "bg-primary-soft" : ""
										}`}
									>
										<span className="text-sm font-medium text-content">{actionWords(row)}</span>
										<span className="text-xs text-muted">
											{originWords(row)} · {expiresWords(row.expiresAt, now)}
										</span>
									</button>
								</li>
							))}
						</ul>
					</Panel>

					<Panel
						title={HISTORY_HEADING}
						footer={
							historyShown.pages > 1 ? (
								<Pager shown={historyShown} onPage={setHistoryPage} />
							) : undefined
						}
					>
						{queue.state === "ready" &&
							queue.data &&
							!queue.data.unavailable &&
							decided.length === 0 && <p className="text-sm text-muted">{HISTORY_EMPTY}</p>}
						<ul className="grid gap-2 text-sm">
							{historyShown.rows.map((row) => (
								<li key={row.id}>
									<span className="text-content">{actionWords(row)}</span> —{" "}
									{statusWords(row.status)}
									{row.decidedBy && ` · ${row.decidedBy}`}
									{row.decidedAt && ` · ${row.decidedAt}`}
									{resultWords(row.result) && ` · ${resultWords(row.result)}`}
									{answers[row.id] && ` · ${answers[row.id].text}`}
								</li>
							))}
						</ul>
					</Panel>
				</div>

				<Panel
					title={chosen ? actionWords(chosen) : APPROVALS_TITLE}
					aside={chosen && <Badge variant="default">{originWords(chosen)}</Badge>}
					actions={
						chosen &&
						waiting.includes(chosen) && (
							<>
								<Button onClick={() => void decide(chosen, "approve")} loading={busy === chosen.id}>
									Approve
								</Button>
								<Button
									variant="outline"
									onClick={() => void decide(chosen, "reject")}
									disabled={busy === chosen.id}
								>
									Reject
								</Button>
							</>
						)
					}
				>
					{!chosen && <p className="text-sm text-muted">{NOTHING_CHOSEN}</p>}
					{chosen && waiting.includes(chosen) && (
						<>
							<p className="text-sm text-muted">{APPROVE_SENDS}</p>
							<p className="text-xs text-muted">{NOT_YOUR_OWN}</p>
						</>
					)}
					{chosen && (
						<>
							<span className="text-xs text-muted">
								{waiting.includes(chosen)
									? expiresWords(chosen.expiresAt, now)
									: statusWords(chosen.status)}
							</span>
							<ul className="list-disc pl-5 text-sm text-content">
								{changeLines(chosen).map((line) => (
									<li key={line}>{line}</li>
								))}
							</ul>
							<p className="text-sm text-muted">{chosen.reason}</p>
							{resultWords(chosen.result) && (
								<p className="text-sm text-muted">{resultWords(chosen.result)}</p>
							)}
							{answers[chosen.id] && (
								<p className="text-sm text-muted">{answers[chosen.id].text}</p>
							)}
						</>
					)}
				</Panel>
			</div>
		</Page>
	);
}
